import { CellValue, RowValues } from 'exceljs';
import { DataSource, DXDS } from '../common/devexpress/dx-data-source';
import type { dxDataGrid } from '../common/devexpress/Grid/dx-grid';
import { isEmpty } from '../components/measurements/copy-datablock-field/common';

export class ExcelParsingError extends Error {
  constructor(message: string) {
    super(message);
  }
}

type DictionaryItem = {
  id: number;
  translatedTitle: string;
};

const dictionaryField = 'dictionaryId';

function getLookupDataSource(
  lookupDataSource: any,
  currentRow: any,
): DataSource | undefined {
  if (!lookupDataSource) {
    return undefined;
  }

  if (Array.isArray(lookupDataSource)) {
    return new DXDS.DataSource(lookupDataSource);
  } else if (typeof lookupDataSource === 'function') {
    return new DXDS.DataSource((lookupDataSource as any)({ data: currentRow }));
  }
  return undefined;
}

async function getLookupValues(
  lookup: any,
  currentRow: any,
): Promise<any | undefined> {
  const dataSource = getLookupDataSource(lookup.dataSource, currentRow);
  if (!dataSource) {
    return undefined;
  }
  dataSource.paginate(false);
  const data = (await dataSource.load()) as any[];
  if (lookup.valueExpr && lookup.displayExpr) {
    return Object.fromEntries(
      data.map((x) => [x[lookup.displayExpr], x[lookup.valueExpr]]),
    );
  }
  return Object.fromEntries(data.map((x) => [x, x]));
}

export async function mapExcelRowToDataGrid<T>(
  dataGrid: dxDataGrid,
  row: RowValues,
): Promise<T> {
  if (!row) {
    throw new ExcelParsingError(`Row is not defined`);
  }
  const result: any = {};
  for (let j = 0; j < dataGrid.columnCount(); j++) {
    const column = dataGrid.columnOption(j);
    if (!column.visible || !column.dataType) {
      continue;
    }
    const dataField = column.dataField as string;
    if (!dataField) {
      continue;
    }
    const caption = column.caption;
    let value = (row as CellValue[])[j] as any;
    const dataType = column.dataType;

    if (value === null || value === undefined) {
      if (dataType === 'string') {
        value = '';
      }
      // else if (dataField === dictionaryField) {
      //   continue;
      // } else {
      //   throw new ExcelParsingError(
      //     `Column ${j} is null/undefined, but expected to have a value for ${caption}`,
      //   );
      // }
    }

    const lookup = column.lookup;
    if (lookup) {
      if (isEmpty(value)) {
        continue;
      }
      if (dataField === dictionaryField) {
        const dictionaries = lookup.items as DictionaryItem[];
        const dictionary = dictionaries.find(
          (x) => x.translatedTitle === value,
        );
        value = dictionary?.id;
      } else {
        const allowedValuesObject = await getLookupValues(lookup, result);
        if (allowedValuesObject) {
          if (!allowedValuesObject[value]) {
            console.log('allowedValuesObject', allowedValuesObject);
            throw new ExcelParsingError(
              `Value '${value}' is not in allowed list of values for ${caption}`,
            );
          }
          value = allowedValuesObject[value];
        }
      }
    }

    if (dataType === 'string') {
      result[dataField] = (value as string).toString();
    } else if (dataType === 'number') {
      const numericValue = parseNumber(value);
      if (isNaN(numericValue)) {
        throw new ExcelParsingError(
          `Value '${value}' is not a number, while ${caption} should be a number`,
        );
      }
      result[dataField] = numericValue;
    } else if (dataType === 'boolean') {
      if (typeof value === 'boolean') {
        result[dataField] = value;
      } else if (value.formula) {
        // this is the case of LibreOffice
        if (value.formula.toLowerCase().includes('true')) {
          result[dataField] = true;
        } else {
          result[dataField] = false;
        }
      } else if (value.toLowerCase() === 'true') {
        result[dataField] = true;
      } else if (value.toLowerCase() === 'false') {
        result[dataField] = false;
      } else {
        throw new ExcelParsingError(
          `Value '${value}' is not boolean, while ${caption} should be a boolean`,
        );
      }
    }
  }
  return result as T;
}

function parseNumber(value: any) {
  if (value === null || value === undefined || value === '') {
    return NaN;
  }

  const stringValue = value.toString();
  if (
    stringValue.includes(' ') ||
    stringValue.includes('\t') ||
    stringValue.includes('\n') ||
    stringValue.includes('\r')
  ) {
    return NaN;
  }

  const num = Number(stringValue);

  return Number.isNaN(num) ? NaN : num;
}
